import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaBook, 
  FaVideo, 
  FaQuestionCircle, 
  FaHeadset, 
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaRocket,
  FaUsers,
  FaCog,
  FaChartLine
} from 'react-icons/fa';
import PublicNav from '../../components/PublicNav/PublicNav';
import PublicFooter from '../../components/PublicFooter/PublicFooter';
import './Support.css';

const Support = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const supportOptions = [
    {
      icon: <FaBook />,
      title: 'Documentation',
      description: 'Comprehensive guides and API references',
      link: '#documentation'
    },
    {
      icon: <FaVideo />,
      title: 'Video Tutorials',
      description: 'Step-by-step video guides for all features',
      link: '#tutorials'
    },
    {
      icon: <FaQuestionCircle />,
      title: 'FAQ',
      description: 'Answers to frequently asked questions',
      link: '#faq'
    },
    {
      icon: <FaHeadset />,
      title: 'Live Support',
      description: '24/7 customer support for premium users',
      link: '#live-support'
    }
  ];

  const categories = [
    {
      icon: <FaRocket />,
      title: 'Getting Started',
      articles: [
        'Quick Start Guide',
        'Creating Your First Project',
        'Inviting Team Members',
        'Setting Up Your Workspace'
      ]
    },
    {
      icon: <FaUsers />,
      title: 'Team Collaboration',
      articles: [
        'Managing Team Permissions',
        'Real-time Collaboration',
        'Task Assignment',
        'Communication Tools'
      ]
    },
    {
      icon: <FaCog />,
      title: 'Configuration',
      articles: [
        'Account Settings',
        'Project Templates',
        'Custom Fields',
        'Integration Setup'
      ]
    },
    {
      icon: <FaChartLine />,
      title: 'Reports & Analytics',
      articles: [
        'Creating Reports',
        'Dashboard Customization',
        'Exporting Data',
        'Performance Metrics'
      ]
    }
  ];

  const faqs = [
    {
      question: 'How do I get started with the platform?',
      answer: 'Getting started is easy! Sign up for a free account, create your first project, and invite your team members. Our quick start guide will walk you through the essential features.'
    },
    {
      question: 'What subscription plans do you offer?',
      answer: 'We offer Free, Professional, and Enterprise plans. The Free plan includes basic features for small teams, while Professional and Enterprise plans offer advanced features, unlimited projects, and priority support.'
    },
    {
      question: 'Can I import data from other project management tools?',
      answer: 'Yes! We support importing data from popular tools like Jira, Trello, Asana, and more. Our import wizard makes the process simple and straightforward.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. We use industry-standard encryption, regular security audits, and comply with GDPR and SOC 2 standards. Your data is backed up daily and stored in secure data centers.'
    },
    {
      question: 'Do you offer training for teams?',
      answer: 'Yes! Enterprise customers get access to personalized onboarding sessions and training workshops. We also offer video tutorials and documentation for all users.'
    },
    {
      question: 'What integrations are available?',
      answer: 'We integrate with 100+ tools including Slack, Microsoft Teams, Google Workspace, GitHub, GitLab, and many more. Check our integrations page for the full list.'
    },
    {
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes, you can cancel your subscription at any time. Your data will remain accessible, and you can export it before downgrading to the free plan.'
    },
    {
      question: 'How do I contact support?',
      answer: 'You can reach our support team via email, live chat (for premium users), or by submitting a ticket through your dashboard. We typically respond within 24 hours.'
    }
  ];

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="support-page">
      <PublicNav />
      
      {/* Hero Section */}
      <section className="support-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="support-hero-content"
          >
            <h1 className="support-title">How Can We Help?</h1>
            <p className="support-subtitle">
              Find answers, get support, and learn how to make the most of our platform
            </p>
            
            {/* Search Bar */}
            <div className="search-bar">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search for help articles, guides, or FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Support Options */}
      <section className="support-options-section">
        <div className="container">
          <div className="support-options-grid">
            {supportOptions.map((option, index) => (
              <motion.a
                key={index}
                href={option.link}
                className="support-option-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="option-icon">{option.icon}</div>
                <h3 className="option-title">{option.title}</h3>
                <p className="option-description">{option.description}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Help Categories */}
      <section className="categories-section" id="documentation">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Browse by Category</h2>
            <p className="section-subtitle">
              Explore our comprehensive documentation organized by topic
            </p>
          </motion.div>

          <div className="categories-grid">
            {categories.map((category, index) => (
              <motion.div
                key={index}
                className="category-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="category-header">
                  <div className="category-icon">{category.icon}</div>
                  <h3 className="category-title">{category.title}</h3>
                </div>
                <ul className="category-articles">
                  {category.articles.map((article, articleIndex) => (
                    <li key={articleIndex}>
                      <a href="#" className="article-link">{article}</a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section" id="faq">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">
              Quick answers to common questions about our platform
            </p>
          </motion.div>

          <div className="faq-container">
            {filteredFaqs.map((faq, index) => (
              <motion.div
                key={index}
                className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <button
                  className="faq-question-btn"
                  onClick={() => toggleFaq(index)}
                >
                  <span className="faq-question">{faq.question}</span>
                  {expandedFaq === index ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {expandedFaq === index && (
                  <motion.div
                    className="faq-answer"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {faq.answer}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Support CTA */}
      <section className="contact-support-section" id="live-support">
        <div className="container">
          <motion.div
            className="contact-support-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <FaHeadset className="support-cta-icon" />
            <h2 className="support-cta-title">Still Need Help?</h2>
            <p className="support-cta-description">
              Our support team is here to help you with any questions or issues you may have.
            </p>
            <div className="support-cta-buttons">
              <a href="/contact" className="btn-primary">Contact Support</a>
              <a href="#" className="btn-secondary">Schedule a Demo</a>
            </div>
          </motion.div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default Support;

